/**
 * Losses Table Component
 * @see Sprint 13 - Pertes
 * @see sprint-datagrid-migration Phase 2.1
 */

import { useMemo, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import {
  DataGrid,
  Badge,
  textColumn,
  dateTimeColumn,
  numberColumn,
  moneyColumn,
  type DataGridColumn,
  type DataGridSortState,
} from '@bill/ui';
import { useCurrency, useBrandContext } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import type { LossListItem, QueryLossesDto, LossType } from '@bstock/shared';
import {
  LossReason,
  LOSS_REASON_LABELS,
  LOSS_REASON_COLORS,
  LOSS_TYPE_LABELS,
  LOSS_TYPE_COLORS,
} from '@bstock/shared';

type SortField = QueryLossesDto['sortBy'];
type SortOrder = QueryLossesDto['sortOrder'];

const SORT_FIELD_MAP: Record<string, SortField> = {
  eventDate: 'eventDate',
  quantity: 'quantity',
  lossReason: 'lossReason',
};

interface LossesTableProps {
  losses: LossListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  isLoading: boolean;
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onRowClick: (loss: LossListItem) => void;
  onCancel?: (loss: LossListItem) => void;
  onSort: (field: SortField, order: SortOrder) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onFetchAllRows?: () => Promise<LossListItem[]>;
  showFinancials?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

function getReasonVariant(reason: LossReason): 'success' | 'warning' | 'danger' | 'neutral' | 'primary' | 'info' {
  return LOSS_REASON_COLORS[reason] ?? 'neutral';
}

export function LossesTable({
  losses,
  pagination,
  isLoading,
  sortBy,
  sortOrder,
  onRowClick,
  onCancel,
  onSort,
  onPageChange,
  onLimitChange,
  onFetchAllRows,
  showFinancials = true,
  dateFrom,
  dateTo,
}: LossesTableProps) {
  const currency = useCurrency();
  const { brand } = useBrandContext();

  const columns = useMemo<DataGridColumn<LossListItem>[]>(() => {
    const cols: DataGridColumn<LossListItem>[] = [
      dateTimeColumn<LossListItem>({
        id: 'eventDate',
        header: 'Date',
        accessor: (r) => new Date(r.eventDate),
        cell: (r) => (
          <span className={r.isCancelled ? 'line-through opacity-50' : ''}>
            {new Date(r.eventDate).toLocaleString('fr-FR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        ),
      }),
      textColumn<LossListItem>({
        id: 'rawProduct',
        header: 'Produit',
        sortable: false,
        accessor: (r) => r.rawProduct.name,
        ellipsis: true,
        maxWidth: '240px',
        cell: (r) => (
          <div className={r.isCancelled ? 'opacity-50' : ''}>
            <div
              className={`text-sm font-medium text-dark-500 ${r.isCancelled ? 'line-through' : ''}`}
            >
              {r.rawProduct.name}
            </div>
            <div className="text-xs text-neutral-400 font-mono">{r.rawProduct.sku}</div>
          </div>
        ),
      }),
      textColumn<LossListItem>({
        id: 'category',
        header: 'Catégorie',
        sortable: false,
        accessor: (r) => r.rawProduct.category.name,
        cell: (r) => (
          <span className={r.isCancelled ? 'opacity-50' : ''}>
            {r.rawProduct.category.name}
          </span>
        ),
      }),
      textColumn<LossListItem>({
        id: 'lossType',
        header: 'Type',
        sortable: false,
        accessor: (r) => LOSS_TYPE_LABELS[(r.lossType ?? 'RAW') as LossType],
        cell: (r) => {
          const lt = (r.lossType ?? 'RAW') as LossType;
          return <Badge variant={LOSS_TYPE_COLORS[lt]}>{LOSS_TYPE_LABELS[lt]}</Badge>;
        },
      }),
      numberColumn<LossListItem>({
        id: 'quantity',
        header: 'Quantité',
        accessor: 'quantity',
        cell: (r) => (
          <span
            className={`text-sm font-semibold text-danger-600 ${r.isCancelled ? 'line-through opacity-50' : ''}`}
          >
            -{formatQuantity(r.quantity)}
          </span>
        ),
      }),
      textColumn<LossListItem>({
        id: 'unit',
        header: 'Unité',
        sortable: false,
        align: 'center',
        accessor: (r) => r.rawProduct.recipeUnit.symbol,
        cell: (r) => (
          <span className={r.isCancelled ? 'opacity-50' : ''}>
            {r.rawProduct.recipeUnit.symbol}
          </span>
        ),
      }),
      textColumn<LossListItem>({
        id: 'lossReason',
        header: 'Motif',
        accessor: (r) => LOSS_REASON_LABELS[r.lossReason],
        cell: (r) => (
          <div className="flex items-center gap-1.5">
            <Badge variant={getReasonVariant(r.lossReason)}>
              {LOSS_REASON_LABELS[r.lossReason]}
            </Badge>
            {r.isCancelled && <Badge variant="neutral">Annulée</Badge>}
          </div>
        ),
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<LossListItem>({
          id: 'totalValue',
          header: 'Valorisation',
          sortable: false,
          accessor: (r) => -(r.totalValue / 100),
          cell: (r) => (
            <span
              className={`text-sm font-semibold text-danger-600 ${r.isCancelled ? 'line-through opacity-50' : ''}`}
            >
              -{formatCentimes(r.totalValue, currency)}
            </span>
          ),
        })
      );
    }

    cols.push(
      textColumn<LossListItem>({
        id: 'createdByUser',
        header: 'Utilisateur',
        sortable: false,
        accessor: (r) =>
          r.createdByUser
            ? `${r.createdByUser.firstName} ${r.createdByUser.lastName}`
            : '—',
        cell: (r) => (
          <span className={r.isCancelled ? 'opacity-50' : ''}>
            {r.createdByUser
              ? `${r.createdByUser.firstName} ${r.createdByUser.lastName}`
              : '—'}
          </span>
        ),
      })
    );

    if (onCancel) {
      cols.push(
        textColumn<LossListItem>({
          id: 'actions',
          header: '',
          sortable: false,
          exportSkip: true,
          width: '50px',
          cell: (r) => (
            <div className="flex justify-end">
              {!r.isCancelled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Voulez-vous annuler cette perte ?')) {
                      onCancel(r);
                    }
                  }}
                  className="p-1.5 text-neutral-400 hover:text-danger-500 hover:bg-danger-50 rounded-md transition-colors"
                  title="Annuler la perte"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ),
        })
      );
    }

    return cols;
  }, [currency, onCancel, showFinancials]);

  // ---- Sort (server-side, controlled) ------------------------------------
  const sortState = useMemo<DataGridSortState | null>(() => {
    if (!sortBy || !sortOrder) return null;
    const columnId = Object.entries(SORT_FIELD_MAP).find(
      ([, v]) => v === sortBy
    )?.[0];
    if (!columnId) return null;
    return { columnId, direction: sortOrder };
  }, [sortBy, sortOrder]);

  const handleSortChange = useCallback(
    (next: DataGridSortState | null) => {
      if (!next) {
        onSort('eventDate', 'desc');
        return;
      }
      const backendField = SORT_FIELD_MAP[next.columnId];
      if (backendField) onSort(backendField, next.direction);
    },
    [onSort]
  );

  return (
    <DataGrid<LossListItem>
      data={losses}
      columns={columns}
      rowKey={(r) => r.id}
      isLoading={isLoading}
      skeletonRows={8}
      onRowClick={onRowClick}
      currency={currency}
      sortState={sortState}
      onSortChange={handleSortChange}
      exportable={{
        fileName: {
          base: 'pertes',
          brand: brand?.name,
          period: { from: dateFrom, to: dateTo },
        },
        sheetName: 'Pertes',
        position: 'header',
        onFetchAllRows,
      }}
      pagination={{
        page: pagination.page,
        limit: pagination.limit,
        total: pagination.total,
        totalPages: pagination.totalPages,
        onPageChange,
        onLimitChange,
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune perte trouvée
        </div>
      }
    />
  );
}
